import React, { useState, useEffect } from 'react';
import api from '../api/axios';
import { Link } from 'react-router-dom';

const CustomerDetailsModal = ({ userId, isOpen, onClose }) => {
  const [data, setData] = useState(null);
  const [loading, setLoading] = useState(true);

  useEffect(() => {
    const fetchSummary = async () => {
      console.log("🚀 Customer Modal Fetch Initiated. User ID:", userId);
      
      if (!userId || userId === 'undefined') {
        console.error("❌ Aborting Fetch: User ID is missing or invalid.");
        setLoading(false);
        return;
      }

      setLoading(true);
      try {
        const response = await api.get(`/users/admin/customers/${userId}/summary`);
        console.log("✅ Customer Summary API Response:", response.data);
        if (response.data.success) {
          setData(response.data.data);
        }
      } catch (error) {
        console.error("❌ API Fetch Failed for Customer Summary:", {
          message: error.message,
          response: error.response?.data,
          status: error.response?.status,
          url: error.config?.url
        });
      } finally {
        setLoading(false);
      }
    };

    if (isOpen && userId) {
      fetchSummary();
    }
  }, [isOpen, userId]);

  if (!isOpen) return null;

  return (
    <div className="fixed inset-0 z-[100] flex items-center justify-center p-4">
      <div 
        className="absolute inset-0 bg-stone-900/60 backdrop-blur-sm" 
        onClick={onClose}
      />
      
      <div className="relative bg-white w-full max-w-2xl rounded-3xl overflow-hidden shadow-2xl border border-stone-200 animate-in fade-in zoom-in duration-300">
        {/* Header */}
        <div className="px-8 py-6 border-b border-stone-100 flex items-center justify-between bg-stone-50/50">
          <div>
            <h3 className="text-xl font-bold text-[#201D42]">Customer Profile</h3>
            <p className="text-xs text-stone-500 font-medium">Detailed demographic and order history</p>
          </div>
          <button 
            onClick={onClose}
            className="w-10 h-10 rounded-full flex items-center justify-center hover:bg-stone-200 text-stone-500 transition-colors"
          >
            <span className="material-symbols-outlined">close</span>
          </button>
        </div>

        <div className="p-8 overflow-y-auto max-h-[70vh]">
          {loading ? (
            <div className="py-12 flex flex-col items-center justify-center">
              <div className="w-10 h-10 border-4 border-t-transparent border-[#201D42] rounded-full animate-spin mb-4" />
              <p className="text-stone-500 font-black uppercase tracking-widest text-[10px]">Retrieving Divine Data...</p>
            </div>
          ) : data ? (
            <div className="space-y-8">
              {/* Demographics Card */}
              <div className="grid grid-cols-2 gap-4">
                <div className="bg-stone-50 p-4 rounded-2xl border border-stone-100">
                  <p className="text-[10px] font-black uppercase tracking-widest text-stone-400 mb-2">Personal Info</p>
                  <div className="space-y-3">
                    <div className="flex items-center gap-3">
                      <span className="material-symbols-outlined text-stone-400 text-lg">person</span>
                      <p className="text-sm font-bold text-stone-800">{data.user.name}</p>
                    </div>
                    <div className="flex items-center gap-3">
                      <span className="material-symbols-outlined text-stone-400 text-lg">mail</span>
                      <p className="text-xs text-stone-500">{data.user.email}</p>
                    </div>
                  </div>
                </div>

                <div className="bg-stone-50 p-4 rounded-2xl border border-stone-100">
                  <p className="text-[10px] font-black uppercase tracking-widest text-stone-400 mb-2">Demographics</p>
                  <div className="space-y-3">
                    <div className="flex items-center gap-3">
                      <span className="material-symbols-outlined text-stone-400 text-lg">wc</span>
                      <p className="text-sm font-bold text-stone-800">{data.user.gender || 'Not Specified'}</p>
                    </div>
                    <div className="flex items-center gap-3">
                      <span className="material-symbols-outlined text-stone-400 text-lg">location_on</span>
                      <p className="text-sm font-bold text-stone-800">{data.user.location || 'Unknown'}</p>
                    </div>
                  </div>
                </div>
              </div>

              {/* Status Badge */}
              <div className="flex items-center gap-3">
                <div className={`px-4 py-2 rounded-full font-black uppercase tracking-widest text-[10px] ${data.isReturning ? 'bg-green-100 text-green-700' : 'bg-blue-100 text-blue-700'}`}>
                  {data.isReturning ? 'Returning Customer' : 'First Time Customer'}
                </div>
                <div className="bg-stone-100 px-4 py-2 rounded-full font-black uppercase tracking-widest text-[10px] text-stone-600">
                  Total Orders: {data.totalOrders}
                </div>
              </div>

              {/* Order History */}
              <div>
                <h4 className="text-sm font-black uppercase tracking-widest text-stone-400 mb-4 flex items-center gap-2">
                  <span className="material-symbols-outlined text-lg">history</span>
                  Order History
                </h4>
                <div className="border border-stone-100 rounded-2xl overflow-hidden shadow-sm">
                  <table className="w-full text-left text-sm">
                    <thead className="bg-stone-50 text-[10px] font-black uppercase tracking-widest text-stone-400">
                      <tr>
                        <th className="px-4 py-3">Date</th>
                        <th className="px-4 py-3">Product</th>
                        <th className="px-4 py-3">Amount</th>
                        <th className="px-4 py-3 text-right">Status</th>
                      </tr>
                    </thead>
                    <tbody className="divide-y divide-stone-50">
                      {data.orderHistory.map((order) => (
                        <tr key={order._id}>
                          <td className="px-4 py-4 text-xs text-stone-500">
                            {new Date(order.createdAt).toLocaleDateString()}
                          </td>
                          <td className="px-4 py-4 font-bold text-stone-800">
                            {order.productId?.name || 'Devotional Item'}
                          </td>
                          <td className="px-4 py-4 text-[#201D42] font-black">
                             ₹{order.productId?.price || 0}
                          </td>
                          <td className="px-4 py-4 text-right">
                             <span className="text-[10px] font-black uppercase tracking-tighter px-2 py-1 rounded bg-stone-100 text-stone-600">
                               {order.status}
                             </span>
                          </td>
                        </tr>
                      ))}
                    </tbody>
                  </table>
                </div>
              </div>
              
              <div className='mt-6 pt-4 border-t border-stone-100 flex justify-end'>
                <Link to='/users' onClick={onClose} className="bg-stone-800 text-white px-5 py-2 rounded-xl font-bold text-xs hover:bg-black transition-colors flex items-center gap-2">
                  View in Users List <span className="material-symbols-outlined text-[14px]">arrow_forward</span>
                </Link>
              </div>
            </div>
          ) : (
            <div className="text-center py-12 text-stone-400">No profile data available.</div>
          )}
        </div>
      </div>
    </div>
  );
};

export default CustomerDetailsModal;
